import { useMemo } from 'react';

import { Skeleton } from '@/components/ui/skeleton';

import { CELL_GROUP_HEIGHT_PX, CELL_HEIGHT_PX, CELL_WIDTH_REM, REM_TO_PX } from '../../constants';
import usePlannerStore from '../../store';

const SKELETON_ROW_COUNT = 25;
const SKELETON_DAY_COUNT = 90;
const SKELETON_BAR_HEIGHT_PX = 24;
const SKELETON_BAR_VERTICAL_GAP_PX = 4;

interface SkeletonBar {
  /** Start position in day-cells from the left edge */
  startDay: number;
  /** Width in day-cells */
  widthDays: number;
  /** Vertical level (0 = first row of bars, 1 = second, etc.) */
  level: number;
}

/**
 * Deterministic pseudo-random number generator seeded by row index.
 * Produces consistent layouts across re-renders without React state.
 */
function seededRandom(seed: number): () => number {
  let state = seed;
  return () => {
    state = (state * 1103515245 + 12345) & 0x7fffffff;
    return (state >>> 16) / 32767;
  };
}

/**
 * Generates skeleton assignment bars for a given row.
 * Produces 2-6 bars with varied positions, widths, and levels to mimic real planner data.
 */
function generateSkeletonBars(rowIndex: number, dayCount: number): SkeletonBar[] {
  const rand = seededRandom(rowIndex * 31 + 7);
  const barCount = Math.floor(rand() * 6) + 2; // 1-3 bars
  const bars: SkeletonBar[] = [];

  for (let i = 0; i < barCount; i++) {
    const widthDays = Math.floor(rand() * 10) + 4; // 4-13 days
    const maxStart = dayCount - widthDays - 1;
    const startDay = Math.floor(rand() * Math.max(1, maxStart));
    const level = Math.floor(rand() * 2); // 0 or 1

    bars.push({ startDay, widthDays, level });
  }

  return bars;
}

interface PlannerSkeletonRowsProps {
  rowCount?: number;
  cellWidth: number;
  dayCount?: number;
  /** Current horizontal scroll offset in px — bars are positioned relative to this */
  scrollLeft?: number;
}

/**
 * Reusable skeleton rows for the planner timeline.
 * Used both in the full-page skeleton and as a loading indicator when fetching more rows.
 *
 * When used for vertical pagination (inline), reads the store's visibleDateRange
 * to only render visible cells with spacers — matching the real rows' horizontal
 * virtualization. This prevents layout shifts caused by DOM width mismatches.
 */
export function PlannerSkeletonRows({
  rowCount = SKELETON_ROW_COUNT,
  cellWidth,
  dayCount = SKELETON_DAY_COUNT,
}: PlannerSkeletonRowsProps) {
  const cellWidthPx = cellWidth * REM_TO_PX;
  const visibleDateRange = usePlannerStore(store => store.visibleDateRange);

  // Use horizontal virtualization when a visible range is set (inline skeleton).
  // Fall back to rendering all cells for the full-page skeleton (no range yet).
  const hasRange = visibleDateRange.startIndex !== 0 || visibleDateRange.endIndex !== 0;
  const startIndex = hasRange ? visibleDateRange.startIndex : 0;
  const endIndex = hasRange ? visibleDateRange.endIndex : dayCount - 1;
  const visibleCellCount = endIndex - startIndex + 1;
  const leftSpacerWidth = startIndex * cellWidth;
  const rightSpacerWidth = Math.max(0, dayCount - endIndex - 1) * cellWidth;

  // Pre-generate bar layouts for all member rows (deterministic, stable across re-renders)
  const rowBars = useMemo(() => {
    const bars: SkeletonBar[][] = [];
    for (let i = 0; i < rowCount; i++) {
      bars.push(generateSkeletonBars(i, dayCount));
    }
    return bars;
  }, [rowCount, dayCount]);

  return (
    <div className="flex flex-col">
      {Array.from({ length: rowCount }).map((_, rowIndex) => {
        const isGroupRow = rowCount > 2 && rowIndex % 5 === 0;
        const rowHeight = isGroupRow ? CELL_GROUP_HEIGHT_PX : CELL_HEIGHT_PX;

        return (
          <div key={`skeleton-row-${rowIndex}`} className="flex min-w-fit">
            {/* Sidebar cell — width MUST match the real row sidebar (timeline-row.tsx:
                w-56 min-w-[14rem]) so the timeline grid lines up with the rows above. */}
            <div
              className="sticky left-0 z-10 flex w-56 min-w-[14rem] items-center border-b border-r bg-background px-4"
              style={{ height: `${rowHeight}px` }}
            >
              <div className="flex items-center gap-2">
                {isGroupRow ? (
                  <Skeleton className="h-4 w-32" />
                ) : (
                  <>
                    <Skeleton className="h-8 w-8 rounded-full" />
                    <div className="flex flex-col gap-1">
                      <Skeleton className="h-3 w-24" />
                      <Skeleton className="h-2 w-16" />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Timeline cells with assignment bars — horizontally virtualized */}
            <div className="relative flex flex-1 overflow-clip">
              {/* Left spacer for cells before the visible window */}
              {leftSpacerWidth > 0 && (
                <div
                  style={{
                    width: `${leftSpacerWidth}rem`,
                    minWidth: `${leftSpacerWidth}rem`,
                    height: `${rowHeight}px`,
                  }}
                />
              )}

              {/* Visible cells only */}
              {Array.from({ length: visibleCellCount }).map((_, cellIndex) => (
                <div
                  key={`skeleton-cell-${rowIndex}-${startIndex + cellIndex}`}
                  className="border-b border-r"
                  style={{
                    width: `${cellWidth}rem`,
                    minWidth: `${cellWidth}rem`,
                    height: `${rowHeight}px`,
                  }}
                />
              ))}

              {/* Right spacer for cells after the visible window */}
              {rightSpacerWidth > 0 && (
                <div
                  style={{
                    width: `${rightSpacerWidth}rem`,
                    minWidth: `${rightSpacerWidth}rem`,
                    height: `${rowHeight}px`,
                  }}
                />
              )}

              {/* Skeleton assignment bars — only render bars within visible range */}
              {!isGroupRow &&
                rowBars[rowIndex]
                  ?.filter(bar => {
                    const barEnd = bar.startDay + bar.widthDays;
                    return barEnd > startIndex && bar.startDay < endIndex;
                  })
                  .map((bar, barIndex) => {
                    const topOffset =
                      8 + bar.level * (SKELETON_BAR_HEIGHT_PX + SKELETON_BAR_VERTICAL_GAP_PX);

                    return (
                      <Skeleton
                        key={`skeleton-bar-${rowIndex}-${barIndex}`}
                        className="absolute rounded-[100px]"
                        style={{
                          left: `${bar.startDay * cellWidthPx}px`,
                          width: `${bar.widthDays * cellWidthPx}px`,
                          height: `${SKELETON_BAR_HEIGHT_PX}px`,
                          top: `${topOffset}px`,
                        }}
                      />
                    );
                  })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function PlannerSkeleton() {
  const zoomLevel = usePlannerStore(store => store.zoomLevel);

  const cellWidth = CELL_WIDTH_REM * zoomLevel;

  return (
    <div className="relative h-full w-full max-w-full">
      <div className="absolute inset-0 h-[94dvh] overflow-x-auto overflow-y-auto pb-12">
        {/* Header Skeleton */}
        <div
          className="top-0 flex h-[6.25rem] min-w-fit bg-background"
          style={{ position: 'sticky', left: 0, top: 0, zIndex: 15 }}
        >
          {/* Sidebar header skeleton — width matches the real sidebar header
              (timeline-header.tsx: w-56 min-w-[14rem]). */}
          <div className="sticky left-0 top-0 z-10 w-56 min-w-[14rem] border-b border-r bg-background p-4">
            <Skeleton className="h-4 w-20" />
          </div>

          {/* Timeline header skeleton */}
          <div className="h-[6.25rem] flex-1">
            {/* Month row */}
            <div className="flex items-center">
              {Array.from({ length: 3 }).map((_, monthIndex) => (
                <div
                  key={`skeleton-month-${monthIndex}`}
                  className="relative flex h-11 items-center justify-center border-b border-r p-1"
                  style={{
                    width: `${30 * cellWidth}rem`,
                    minWidth: `${30 * cellWidth}rem`,
                  }}
                >
                  <Skeleton className="h-4 w-24" />
                </div>
              ))}
            </div>

            {/* Day row */}
            <div className="flex h-14">
              {Array.from({ length: SKELETON_DAY_COUNT }).map((_, dayIndex) => (
                <div
                  key={`skeleton-day-${dayIndex}`}
                  className="relative border-b border-r py-2 text-center"
                  style={{
                    width: `${cellWidth}rem`,
                    minWidth: `${cellWidth}rem`,
                  }}
                >
                  <Skeleton className="mx-auto mb-1 h-3 w-4" />
                  <Skeleton className="mx-auto h-2 w-6" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Rows Skeleton */}
        <PlannerSkeletonRows cellWidth={cellWidth} />
      </div>
    </div>
  );
}
